import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { LogOut, Plane } from 'lucide-react';
import { toast } from 'sonner';
import NationalityManager from '../components/NationalityManager';
import VisaTypeManager from '../components/VisaTypeManager';
import SpecialRuleManager from '../components/SpecialRuleManager';
import VisaExtraFeeManager from '../components/VisaExtraFeeManager';
import ChangePassword from '../components/ChangePassword';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('nationalities');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-blue-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Plane className="w-8 h-8" />
              <div>
                <h1 className="text-2xl font-bold">White Sky Travel</h1>
                <p className="text-blue-100 text-sm">Admin Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                View Site
              </Button>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20 flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-8">
            <TabsTrigger value="nationalities" data-testid="tab-nationalities">Nationalities</TabsTrigger>
            <TabsTrigger value="visa-types" data-testid="tab-visa-types">Visa Types</TabsTrigger>
            <TabsTrigger value="extra-fees" data-testid="tab-extra-fees">Extra Fees</TabsTrigger>
            <TabsTrigger value="special-rules" data-testid="tab-special-rules">Special Rules</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="nationalities">
            <NationalityManager api={API} getAuthHeader={getAuthHeader} />
          </TabsContent>

          <TabsContent value="visa-types">
            <VisaTypeManager api={API} getAuthHeader={getAuthHeader} />
          </TabsContent>

          <TabsContent value="extra-fees">
            <VisaExtraFeeManager api={API} getAuthHeader={getAuthHeader} />
          </TabsContent>

          <TabsContent value="special-rules">
            <SpecialRuleManager api={API} getAuthHeader={getAuthHeader} />
          </TabsContent>

          <TabsContent value="settings">
            <ChangePassword getAuthHeader={getAuthHeader} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
